import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { MerchandiseMovementService } from '../services/merchandiseMovementService';
import { toast } from 'sonner';
import type {
  MerchandiseMovement,
  MerchandiseMovementFormData,
  MerchandiseMovementFilters,
  MerchandiseMovementStats
} from '@/types/merchandise-movement.types';

const getWeekRange = () => {
  const now = new Date();
  const day = now.getDay(); // 0=domingo, 1=lunes...
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(now);
  monday.setDate(now.getDate() + diffToMonday);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const fmt = (d: Date) => d.toISOString().split('T')[0];
  return { dateFrom: fmt(monday), dateTo: fmt(sunday) };
};

const initialFilters: MerchandiseMovementFilters = {
  direction: 'all',
  locationId: 'all',
  ingredientId: 'all',
  movementType: 'all',
  status: 'all',
  ...getWeekRange(),
  requestedBy: ''
};

export const useMerchandiseMovements = () => {
  const { selectedLocation } = useAuth();
  const userLocationId = selectedLocation?.id?.toString() || 'all';

  const [merchandiseMovements, setMerchandiseMovements] = useState<MerchandiseMovement[]>([]);
  const [stats, setStats] = useState<MerchandiseMovementStats | null>(null);
  const [filters, setFilters] = useState<MerchandiseMovementFilters>({
    ...initialFilters,
    locationId: userLocationId
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load merchandise movements from API
  const loadMerchandiseMovements = useCallback(async (filtersToApply?: MerchandiseMovementFilters) => {
    try {
      setLoading(true);
      setError(null);

      const movements = await MerchandiseMovementService.getMerchandiseMovements(filtersToApply || filters);
      setMerchandiseMovements(movements);
    } catch (err) {
      console.error('Error loading merchandise movements:', err);
      setError(err instanceof Error ? err.message : 'Error al cargar los movimientos de mercadería');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load stats from API
  const loadStats = useCallback(async () => {
    try {
      const statsData = await MerchandiseMovementService.getMerchandiseMovementStats();
      setStats(statsData);
    } catch (err) {
      console.error('Error loading stats:', err);
      // Don't show error for stats, just log it
    }
  }, []);

  // Load data only on mount (with user's location filter)
  useEffect(() => {
    const defaultFilters = { ...initialFilters, locationId: userLocationId };
    loadMerchandiseMovements(defaultFilters);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load stats on mount
  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Filter merchandise movements based on current filters (client-side filtering for search)
  const filteredMerchandiseMovements = useMemo(() => {
    if (!filters.ingredientId || filters.ingredientId === 'all') {
      return merchandiseMovements;
    }

    // Apply client-side ingredient search filter
    const searchTerm = filters.ingredientId.toLowerCase();
    return merchandiseMovements.filter(movement => {
      const ingredientName = movement.ingredientName?.toLowerCase() || '';
      const ingredientId = movement.ingredientId?.toLowerCase() || '';
      return ingredientName.includes(searchTerm) || ingredientId.includes(searchTerm);
    });
  }, [merchandiseMovements, filters.ingredientId]);

  // Update filters (solo actualiza estado local, no dispara búsqueda)
  const updateFilters = useCallback((newFilters: Partial<MerchandiseMovementFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  // Fetch movements con los filtros actuales (se llama desde botón Buscar)
  const fetchMovements = useCallback(async (filtersToApply?: Partial<MerchandiseMovementFilters>) => {
    const mergedFilters = filtersToApply ? { ...filters, ...filtersToApply } : filters;
    await loadMerchandiseMovements(mergedFilters as MerchandiseMovementFilters);
    await loadStats();
  }, [filters, loadMerchandiseMovements, loadStats]);

  // Create new merchandise movement
  const createMerchandiseMovement = useCallback(async (movementData: MerchandiseMovementFormData): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      const newMovement = await MerchandiseMovementService.createMerchandiseMovement(movementData);

      // Add to local state
      setMerchandiseMovements(prev => [newMovement, ...prev]);

      // Reload stats
      await loadStats();

      return true;
    } catch (err) {
      console.error('Error creating merchandise movement:', err);
      toast.error(err instanceof Error ? err.message : 'Error al crear el movimiento de mercadería');
      return false;
    } finally {
      setLoading(false);
    }
  }, [loadStats]);

  // Approve merchandise movement
  const approveMerchandiseMovement = useCallback(async (movementId: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      const updatedMovement = await MerchandiseMovementService.approveMerchandiseMovement(movementId);

      // Update local state - merge with existing data since approve endpoint returns partial data
      setMerchandiseMovements(prev => prev.map(movement =>
        movement.id === movementId ? { ...movement, ...updatedMovement } : movement
      ));

      // Reload stats
      await loadStats();

      return true;
    } catch (err) {
      console.error('Error approving merchandise movement:', err);
      toast.error(err instanceof Error ? err.message : 'Error al aprobar el movimiento de mercadería');
      return false;
    } finally {
      setLoading(false);
    }
  }, [loadStats]);

  // Reject merchandise movement
  const rejectMerchandiseMovement = useCallback(async (movementId: string, rejectionReason: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      const updatedMovement = await MerchandiseMovementService.rejectMerchandiseMovement(movementId, rejectionReason);

      // Update local state - merge with existing data since reject endpoint returns partial data
      setMerchandiseMovements(prev => prev.map(movement =>
        movement.id === movementId ? { ...movement, ...updatedMovement } : movement
      ));

      // Reload stats
      await loadStats();

      return true;
    } catch (err) {
      console.error('Error rejecting merchandise movement:', err);
      toast.error(err instanceof Error ? err.message : 'Error al rechazar el movimiento de mercadería');
      return false;
    } finally {
      setLoading(false);
    }
  }, [loadStats]);

  // Cancel merchandise movement
  const cancelMerchandiseMovement = useCallback(async (movementId: string, cancellationReason: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      const updatedMovement = await MerchandiseMovementService.cancelMerchandiseMovement(movementId, cancellationReason);

      // Update local state - merge with existing data since cancel endpoint returns partial data
      setMerchandiseMovements(prev => prev.map(movement =>
        movement.id === movementId ? { ...movement, ...updatedMovement } : movement
      ));

      // Reload stats
      await loadStats();

      return true;
    } catch (err) {
      console.error('Error cancelling merchandise movement:', err);
      toast.error(err instanceof Error ? err.message : 'Error al cancelar el movimiento de mercadería');
      return false;
    } finally {
      setLoading(false);
    }
  }, [loadStats]);

  // Refresh data
  const refresh = useCallback(async () => {
    await Promise.all([loadMerchandiseMovements(), loadStats()]);
  }, [loadMerchandiseMovements, loadStats]);

  return {
    merchandiseMovements: filteredMerchandiseMovements,
    stats,
    filters,
    loading,
    error,
    updateFilters,
    fetchMovements,
    createMerchandiseMovement,
    approveMerchandiseMovement,
    rejectMerchandiseMovement,
    cancelMerchandiseMovement,
    refresh
  };
};